import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { useSession } from '../lib/session';

export function PasswordGate() {
  const { refresh } = useSession();
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/auth/login', { password });
      await refresh();
    } catch {
      setError("That password didn't work. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-pine text-cream flex items-center justify-center px-6">
      <form onSubmit={onSubmit} className="w-full max-w-sm">
        <h1 className="font-serif text-4xl text-center mb-2">Welcome</h1>
        <p className="text-cream/70 text-base text-center mb-8">
          Enter the password from your invitation.
        </p>
        <div className="relative">
          <input
            type={show ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            className="w-full bg-pine border border-cream/30 py-3 pl-4 pr-12 font-sans text-cream placeholder-cream/40 focus:outline-none focus:border-dusty-rose"
            placeholder="Password"
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            aria-label={show ? 'Hide password' : 'Show password'}
            aria-pressed={show}
            className="absolute inset-y-0 right-0 flex items-center px-4 text-cream/50 transition-colors hover:text-cream focus:outline-none focus-visible:text-cream"
          >
            <EyeIcon off={show} />
          </button>
        </div>
        {error && <p className="text-dusty-rose text-base mt-3">{error}</p>}
        <button
          type="submit"
          disabled={submitting || !password}
          className="mt-6 w-full bg-cream py-3 text-base font-medium uppercase tracking-[0.2em] text-pine hover:bg-blush transition-colors disabled:opacity-50"
        >
          {submitting ? '…' : 'Enter'}
        </button>
      </form>
    </div>
  );
}

/** Open eye, or eye with a slash through it when the password is revealed. */
function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1.5 12s4-7 10.5-7 10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12Z" />
      <circle cx="12" cy="12" r="3" />
      {off && <line x1="3" y1="3" x2="21" y2="21" />}
    </svg>
  );
}
